import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, TrendingUp, Users, Heart, Target, MessageSquare, Calendar, CheckCircle, ArrowRight, Star, Linkedin, Instagram, Youtube, User, Phone, Mail, Send, Loader2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const Kariera = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formState, setFormState] = useState({ name: '', email: '', phone: '', message: '' });
  const normalizePhone = (value) => String(value || '').replace(/\s+/g, '');

  const benefits = [
    { num: 1, title: 'Mentoring 1:1', desc: 'Pravidelné vedenie od Peťa. Bez pozlátka. Reálna práca na reálnych prípadoch.', image: '/kariera_1z6.jpeg' },
    { num: 2, title: 'Unikátne know-how', desc: 'Prístup k systému budovania renty a nástrojom Majetkového poriadku.', image: '/kariera_2z6.jpeg' },
    { num: 3, title: 'Osobná značka', desc: 'Podpora pri budovaní vášho mena pod záštitou silného projektu.', image: '/kariera_3z6.jpeg' },
    { num: 4, title: 'Kvalitná klientela', desc: 'Získate prístup k bonitným klientom, ktorí rozumejú hodnote poradenstva.', image: '/kariera_4z6.jpeg' },
    { num: 5, title: 'Technológie a Guidance', desc: 'Video podpora, marketingové materiály a vyladené procesy.', image: '/kariera_5z6.jpeg' },
    { num: 6, title: 'Sloboda vs Zodpovednosť', desc: 'Žiadny mikro-manažment. Máte priestor, ale očakávame výsledky.', image: '/kariera_6z6.jpeg' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/ecomail-career', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formState.name,
          email: formState.email,
          phone: normalizePhone(formState.phone),
          message: formState.message,
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody?.error || 'Chyba pri odosielaní.');
      }
      toast.success('Správa úspešne odoslaná. Budeme vás kontaktovať!');
      setFormState({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error(error.message || 'Chyba pri odosielaní. Skúste to neskôr.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="kariera-page font-body text-brand-dark-blue overflow-x-hidden">
      {/* 1. HERO - PREMIOVY */}
      <section className="relative bg-brand-dark-blue text-white min-h-[90vh] flex items-center overflow-x-clip">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url(/symbol-3.svg)', backgroundSize: '400px', backgroundRepeat: 'repeat' }} />
        <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-brand-orange/10 rounded-full blur-[150px] pointer-events-none" />

        <div className="container mx-auto px-4 sm:px-5 relative z-10 max-w-full min-w-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center min-w-0">
            <div className="max-w-xl min-w-0">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/10 text-brand-orange text-sm font-bold uppercase tracking-wide mb-8">
                <Star size={14} className="fill-brand-orange" />
                Pridaj sa k lídrom
              </div>
              <h1 className="text-[1.75rem] min-[400px]:text-3xl sm:text-4xl lg:text-7xl font-heading font-bold mb-10 text-white leading-[1.12] sm:leading-tight break-words">
                Zmeňte kariéru na poslanie
              </h1>

              <p className="text-base sm:text-xl lg:text-2xl text-gray-200 mb-12 leading-relaxed font-light">
                Hľadáme do tímu partnerov, ktorí nechcú predávať produkty, ale budovať dlhodobú hodnotu pre klientov aj pre seba.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a href="#kontakt" className="inline-flex items-center justify-center bg-brand-orange hover:bg-red-600 text-white font-bold py-5 px-10 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-xl text-lg">
                  <Briefcase size={24} className="mr-3" />
                  Chcem sa pridať
                </a>
                <Link
                  to="/konzultace"
                  className="inline-flex items-center justify-center bg-white/10 hover:bg-white text-white hover:text-brand-dark-blue font-bold py-5 px-10 rounded-2xl transition-all duration-300 border border-white/20 text-lg"
                >
                  <Calendar className="mr-3" />
                  Rezervovať call
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl skew-y-1">
                <img src="/kariera_hero.jpeg" alt="Kariera Hero" className="w-full h-full object-cover aspect-[4/5]" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark-blue/60 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. EMOTIONAL GRID SECTION */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-16 items-center max-w-6xl mx-auto">
            <div className="w-full md:w-1/2 grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img src="/kariera_1z4.jpeg" className="rounded-2xl shadow-lg h-64 w-full object-cover" alt="Vibe 1" />
                <img src="/kariera_3z4.jpeg" className="rounded-2xl shadow-lg h-80 w-full object-cover" alt="Vibe 2" />
              </div>
              <div className="space-y-4 pt-12">
                <img src="/kariera_2z4.jpeg" className="rounded-2xl shadow-lg h-80 w-full object-cover" alt="Vibe 3" />
                <img src="/kariera_4z4.jpeg" className="rounded-2xl shadow-lg h-64 w-full object-cover" alt="Vibe 4" />
              </div>
            </div>
            <div className="w-full md:w-1/2 space-y-8">
              <h2 className="text-4xl lg:text-5xl font-heading font-bold text-brand-dark-blue">
                Nie sme korporát.<br />Sme rodina profesionálov.
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                V mojom tíme sa neriešia ranné reporty ani nátlakové kampane. Rieši sa to, ako urobiť z klienta rentiera a z vás rešpektovaného odborníka.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-6 bg-gray-50 rounded-2xl border border-gray-100 italic font-medium">
                  "Nezaujíma ma, či viete predávať. Zaujíma ma, ako viete rozmýšľať."
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. BENEFITS CARDS */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-heading font-bold text-brand-dark-blue mb-6">
              Čo ti spolupráca prinesie
            </h2>
            <p className="text-xl text-gray-600">Investujeme do vašej hlavy, vašej značky a vášho pokoja.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {benefits.map(benefit => (
              <div key={benefit.num} className="bg-white rounded-[2.5rem] overflow-hidden shadow-xl hover:shadow-2xl transition-all group flex flex-col h-full border border-gray-100">
                <div className="h-64 relative overflow-hidden">
                  <img src={benefit.image} alt={benefit.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute top-6 left-6 bg-brand-orange text-white w-12 h-12 rounded-2xl flex items-center justify-center text-xl font-bold shadow-lg">
                    {benefit.num}
                  </div>
                </div>

                <div className="p-8 flex-grow">
                  <h3 className="text-2xl font-heading font-bold mb-4 text-brand-dark-blue group-hover:text-brand-orange transition-colors">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    {benefit.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. WORKFLOW / HOW IT WORKS */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-heading font-bold text-brand-dark-blue">
              Ako prebieha zapojenie
            </h2>
          </div>

          <div className="space-y-12">
            {[
              { num: '01', title: 'Úvodný call (15 min)', desc: 'Krátko si povieme, kto sme a čo hľadáme. Ak je tam chémia, ideme ďalej.', image: '/kariera_1z3.jpeg' },
              { num: '02', title: 'Osobné stretnutie', desc: 'Prejdeme si do detailu naše systémy, provízie a víziu spolupráce.', image: '/kariera_2z3.jpeg' },
              { num: '03', title: 'Zapracovanie', desc: 'Dostanete mentora, materiály a začínate budovať vlastné klientské kmene.', image: '/kariera_3z3.jpeg' }
            ].map((step, i) => (
              <div key={i} className={`flex flex-col ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-12 items-center bg-gray-50 rounded-[3rem] p-8 lg:p-12 hover:bg-orange-50/30 transition-colors border border-gray-100`}>
                <div className="w-full md:w-1/2 h-80 rounded-[2rem] overflow-hidden shadow-lg">
                  <img src={step.image} className="w-full h-full object-cover" alt={step.title} />
                </div>
                <div className="w-full md:w-1/2 space-y-6">
                  <div className="text-6xl font-black text-brand-orange/20 font-heading leading-none">{step.num}</div>
                  <h3 className="text-3xl font-heading font-bold text-brand-dark-blue">{step.title}</h3>
                  <p className="text-xl text-gray-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. UNIFIED FINAL SECTION - FORM & CTA */}
      <section id="kontakt" className="py-32 bg-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-stretch">

            {/* Booking & Story */}
            <div className="flex flex-col space-y-8">
              <div className="flex-grow space-y-8">
                <h2 className="text-4xl lg:text-6xl font-heading font-bold text-brand-dark-blue leading-tight">
                  Pripravení na novú <span className="text-brand-orange">kapitolu?</span>
                </h2>
                <p className="text-2xl text-gray-600 leading-relaxed max-w-md">
                  V mojom tíme budujeme rentné systémy, nie predajné kampane. Ak chcete robiť poradenstvo pre bonitných klientov, ozvite sa.
                </p>
              </div>

              <div className="p-10 bg-brand-dark-blue text-white rounded-[2.5rem] shadow-2xl relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-brand-orange/10 rounded-full blur-3xl group-hover:bg-brand-orange/20 transition-all" />
                <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                  <Calendar className="text-brand-orange" />
                  Priama rezervácia termínu
                </h3>
                <p className="text-gray-400 mb-8 max-w-sm">
                  Vyberte si voľný slot v mojom kalendári na 15-minútový zoznamovací hovor.
                </p>
                <Link
                  to="/konzultace"
                  className="inline-flex items-center justify-center bg-brand-orange text-white font-bold py-5 px-10 rounded-2xl hover:bg-red-600 transition-all w-full text-center text-lg"
                >
                  Pozrieť termíny
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>

              <div className="flex gap-4">
                <a href="/kniha" className="flex-grow inline-flex items-center justify-center gap-2 border-2 border-gray-200 bg-white hover:border-brand-orange p-5 rounded-2xl font-bold text-brand-dark-blue transition-all">
                  <BookOpen size={20} className="text-brand-orange" />
                  Pozrieť moju knihu
                </a>
              </div>
            </div>

            {/* Contact Form Card */}
            <div className="bg-white rounded-[3rem] shadow-2xl p-8 lg:p-14 border border-gray-100 relative">
              <div className="absolute top-6 right-6 text-brand-orange/10">
                <MessageSquare size={120} strokeWidth={1} />
              </div>
              <h3 className="text-3xl font-heading font-bold text-brand-dark-blue mb-10 relative z-10">Napíšte mi o sebe</h3>

              <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                      <User size={14} className="text-brand-orange" /> Meno a priezvisko
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-4 focus:outline-none focus:ring-2 focus:ring-brand-orange transition-all"
                      placeholder="Peter Novák"
                      value={formState.name}
                      onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                        <Phone size={14} className="text-brand-orange" /> Telefón
                      </label>
                      <input
                        type="tel"
                        required
                        className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-4 focus:outline-none focus:ring-2 focus:ring-brand-orange transition-all"
                        placeholder="+421 ..."
                        value={formState.phone}
                        onChange={(e) => setFormState({ ...formState, phone: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                        <Mail size={14} className="text-brand-orange" /> Email
                      </label>
                      <input
                        type="email"
                        required
                        className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-4 focus:outline-none focus:ring-2 focus:ring-brand-orange transition-all"
                        placeholder="email@adresa.sk"
                        value={formState.email}
                        onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                      <MessageSquare size={14} className="text-brand-orange" /> Váš príbeh v kocke
                    </label>
                    <textarea
                      rows="3"
                      className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-4 focus:outline-none focus:ring-2 focus:ring-brand-orange transition-all"
                      placeholder="Čo hľadáte v novej spolupráci?"
                      value={formState.message}
                      onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-brand-dark-blue hover:bg-brand-orange text-white font-bold py-5 rounded-2xl shadow-xl transition-all duration-500 transform hover:scale-[1.02] flex items-center justify-center gap-3 disabled:opacity-50 text-lg"
                >
                  {isSubmitting ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <>
                      <Send size={20} />
                      Odoslať žiadosť
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Kariera;
